import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IVideo } from '@models/IVideo';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';

interface Props {
  readonly video: IVideo;
  readonly title?: string;
  readonly subTitle: string;
  readonly text?: string;
  readonly cta: ILink;
  readonly hideSubtitle?: boolean;
  readonly isHero?: boolean;
  readonly logo?: IImage;
}

const VideoWithTextOver = ({
  video,
  title,
  subTitle,
  text,
  cta,
  hideSubtitle,
  isHero,
  logo,
}: Props) => {
  const isPreview = usePreviewMode();
  const isMobile = useMediaQuery(1024);
  const hasCTA = !!(cta?.url && cta.name);

  return (
    <section
      className="component-padding bg-black-100"
      data-testid="CT-152-Video-With-Text-Over"
    >
      <SoftLoadWrapper>
        <div
          className={classNames(
            'relative',
            isPreview ? 'h-[1080px]' : 'h-screen'
          )}
        >
          <div
            className="absolute inset-0 z-[1] bg-black bg-opacity-40"
            data-testid="CT-152-Video-With-Text-Over-Tint"
          />

          <div
            className="absolute top-0 z-[1] h-1/4 w-full bg-gradient-to-b from-black/100"
            data-testid="CT-152-Video-With-Text-Over-Top-Gradient"
          />

          <div
            className="absolute bottom-0 z-[1] h-1/4 w-full bg-gradient-to-t from-black/100"
            data-testid="CT-152-Video-With-Text-Over-Bottom-Gradient"
          />

          <div className="absolute inset-0 z-[1] flex items-center justify-center text-white">
            <div className="w-full text-center max-md:px-5 md:w-1/2 xl:w-3/5">
              {title && (
                <p
                  data-testid="CT-152-Video-With-Text-Over-Title"
                  className="h3"
                >
                  {title}
                </p>
              )}

              {logo && (
                <Image
                  image={logo}
                  classes={classNames('flex items-center justify-center', {
                    'mb-5 lg:mb-7.5': !hideSubtitle || text || hasCTA,
                  })}
                  imageClasses="max-w-[65%] lg:max-w-[75%]"
                />
              )}

              {isHero && (
                <h1
                  className={classNames(isMobile ? 'mount' : 'strap', {
                    hidden: hideSubtitle,
                    'mb-7.5 lg:mb-16': text || hasCTA,
                  })}
                  data-testid="CT-152-Video-With-Text-Over-Subtitle"
                >
                  {subTitle}
                </h1>
              )}

              {!isHero && !hideSubtitle && (
                <p
                  className={classNames('mount', {
                    'mb-7.5 lg:mb-10': text || hasCTA,
                  })}
                  data-testid="CT-152-Video-With-Text-Over-Subtitle"
                >
                  {subTitle}
                </p>
              )}

              {text && (
                <p
                  className={classNames('h6', { 'mb-5 lg:mb-10': hasCTA })}
                  data-testid="CT-152-Video-With-Text-Over-Text"
                >
                  {text}
                </p>
              )}

              {hasCTA && (
                <Button
                  link={cta}
                  dataTestId="CT-152-Video-With-Text-Over-CTA"
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'selectContent',
                      content_type: 'video_cta',
                      link_text: cta.name,
                    });
                  }}
                />
              )}
            </div>
          </div>

          <InViewVideo
            wrapperClasses="h-full"
            videoClasses="[&>div>video]:object-cover h-full"
            autoPlay
            controls={false}
            video={video}
            inViewThreshold={0}
            hasInVideoPlayButton
            inVideoPlayButtonClassName="z-10"
            dataTestId="CT-152-Video-With-Text-Over-Video-Container"
          />
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default VideoWithTextOver;
